import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setUserData } from "../../redux/actions/userDataActions";

import Breadcrumb from "../components/Breadcrumb";
import { Button } from "../components/Form/Button";
import { Input } from "../components/Form/Input";
import ImprovedDatePicker from "../components/Form/ImprovedDatePicker";
import AuthProvider from "../services/auth/AuthProvider/Index";
import { FormErrorsSchema } from "../types";
import { mergeErrors, validateDateOfBirth as validateDOB } from "../utils";
import notifications from "../services/notifications";

const ProfileUpdate = ({ isAccountSetup = false }) => {
  const [loading, setLoading] = useState(false);
  const [givenName, setGivenName] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [birthdate, setBirthdate] = useState<string>("");
  const [isDateOfBirthValid, setIsDateOfBirthValid] = useState(false);
  const [formErrors, setFormErrors] = useState<FormErrorsSchema>({});
  
  // Shipping address fields
  const [addressLine, setAddressLine] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [stateProvinceCode, setStateProvinceCode] = useState<string>("");
  const [postalCode, setPostalCode] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const dispatchSetUserData = (value: any) => {
        dispatch(setUserData(value));
      };

      setLoading(true);
      const identity = await AuthProvider.getIdentity();

      if (identity) {
        dispatchSetUserData(identity);
        setGivenName(identity.given_name || "");
        setFamilyName(identity.family_name || "");
        setBirthdate(identity.birthdate || "");
        setIsDateOfBirthValid(validateDOB(identity.birthdate));
        
        // Load shipping address from profile if available
        if (identity.shipping_address) {
          const addr = identity.shipping_address;
          setAddressLine(Array.isArray(addr.addressLine) ? addr.addressLine[0] : (addr.addressLine || ""));
          setCity(addr.city || "");
          setStateProvinceCode(addr.stateProvinceCode || "");
          setPostalCode(addr.postalCode || "");
          setPhone(addr.phone || "");
        }
        
        setLoading(false);
      }
    })();
  }, [dispatch]);


  const handleDateOfBirthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDateOfBirth = e.target.value;
    setBirthdate(newDateOfBirth);
    setIsDateOfBirthValid(validateDOB(newDateOfBirth));
  };

  const buildProfileRequestBody = () => {
    const body: any = {
      given_name: givenName,
      family_name: familyName,
    };
  
    if (birthdate?.trim() && isDateOfBirthValid) {
      body.birthdate = birthdate;
    }
  
    const hasAddress =
      addressLine || city || stateProvinceCode || postalCode || phone;
  
    if (hasAddress) {
      body.shipping_address = {
        countryCode: "US",
        ...(addressLine?.trim() && { addressLine }),
        ...(city?.trim() && { city }),
        ...(stateProvinceCode?.trim() && { stateProvinceCode }),
        ...(postalCode?.trim() && { postalCode }),
        ...(phone?.trim() && { phone }),
      };
    }
  
    return body;
  };

  const handleProfileUpdateError = async (response: Response) => {
    if (response.status === 422) {
      const errorData = await response.json();
      setFormErrors(mergeErrors(errorData.errors));
      return;
    }
  
    const errorText = await response.text();
    console.error("API Error:", response.status, errorText);
    throw new Error(`Failed to update profile: ${response.status}`);
  };

  const syncIdentityToState = async () => {
    const identity = await AuthProvider.getIdentity();
    if (!identity) return;
  
    dispatch(setUserData(identity));
  
    setGivenName(identity.given_name || "");
    setFamilyName(identity.family_name || "");
    setBirthdate(identity.birthdate || "");
    setIsDateOfBirthValid(validateDOB(identity.birthdate));
  
    if (identity.shipping_address) {
      const addr = identity.shipping_address;
      setAddressLine(Array.isArray(addr.addressLine) ? addr.addressLine[0] : addr.addressLine || "");
      setCity(addr.city || "");
      setStateProvinceCode(addr.stateProvinceCode || "");
      setPostalCode(addr.postalCode || "");
      setPhone(addr.phone || "");
    }
  };

  const showProfileUpdateSuccess = () => {
    const dobUpdated = birthdate?.trim() && isDateOfBirthValid;
  
    const message = dobUpdated
      ? "Your profile including date of birth has been successfully updated."
      : "Your profile has been successfully updated.";
  
    notifications.success({
      message,
      ...(isAccountSetup && { onReload: true }),
    });
  
    if (isAccountSetup) {
      navigate(0);
    }
  };  

  const handleUpdateProfile = async () => {
    try {
      setLoading(true);
      setFormErrors({});
  
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/v1/private/identity/me`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${await AuthProvider.getValidAuthorizationToken()}`,
          },
          body: JSON.stringify(buildProfileRequestBody()),
        }
      );
  
      if (!response.ok) {
        await handleProfileUpdateError(response);
        return;
      }
  
      await syncIdentityToState();
      showProfileUpdateSuccess();
    } catch (error) {
      console.error("Error fetching data:", error);
      notifications.error({
        message: "Something went wrong while trying to update your profile.",
      });
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="w-full">
      <div className="w-full min-h-screen text-gray-900">
        <main className="mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          {!isAccountSetup && (
            <Breadcrumb
              pages={[
                { name: "My account", href: "#", current: false },
                { name: "Profile update", href: "#", current: true },
              ]}
            />
          )}
          <div className="mt-8">
            <h1 className="text-xl font-semibold">Profile update</h1>
          </div>
          <div className="bg-white mt-6 px-6 pb-6 pt-2">
            <div className="w-full mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-6 items-start">
                {/* Left Column: Personal Information */}
                <div className="space-y-6">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-800 mb-4">Personal Information</h2>
                  </div>
                  <div>
                    <Input
                      label="First name"
                      name="given_name"
                      placeholder=""
                      errors={formErrors}
                      value={givenName}
                      setValue={setGivenName}
                      disabled={loading}
                      required={true}
                      inputMask={(inputText: string) => {
                        return inputText.replaceAll(/[^a-zA-Z0-9 ]/g, "");
                      }}
                    />
                  </div>
                  <div>
                    <Input
                      label="Last name"
                      name="family_name"
                      placeholder=""
                      errors={formErrors}
                      value={familyName}
                      setValue={setFamilyName}
                      disabled={loading}
                      required={true}
                      inputMask={(inputText: string) => {
                        return inputText.replaceAll(/[^a-zA-Z0-9 ]/g, "");
                      }}
                    />
                  </div>
                  <div>
                    <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 mb-2">
                      Date of Birth *
                    </label>
                    <ImprovedDatePicker
                        id="dateOfBirth"
                        value={birthdate || ""}
                        onChange={handleDateOfBirthChange}
                        className="w-full"
                        hasError={!isDateOfBirthValid && !!(birthdate && birthdate.length > 0)}
                        required
                        placeholder="mm/dd/yyyy"
                    />
                    {!isDateOfBirthValid && birthdate && birthdate.length > 0 && (
                        <p className="mt-2 text-sm text-red-500">You must be at least 18 years old to register.</p>
                    )}
                  </div>
                </div>

                {/* Right Column: Shipping Address */}
                <div className="space-y-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-800">Shipping Address</h2>
                    </div>
                  </div>
                  <div>
                    <Input
                      label="Street Address"
                      name="shipping_addressLine"
                      placeholder="Enter your street address"
                      errors={formErrors}
                      value={addressLine}
                      setValue={setAddressLine}
                      disabled={loading}
                      required={true}
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        label="City"
                        name="shipping_city"
                        placeholder="Enter your city"
                        errors={formErrors}
                        value={city}
                        setValue={setCity}
                        disabled={loading}
                        required={true}
                      />
                      <Input
                        label="State"
                        name="shipping_stateProvinceCode"
                        placeholder="Enter state code (e.g., CA)"
                        errors={formErrors}
                        value={stateProvinceCode}
                        setValue={(val: string) => setStateProvinceCode(val.toUpperCase().slice(0, 2))}
                        disabled={loading}
                        required={true}
                        inputMask={(text: string) => text.toUpperCase().slice(0, 2)}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        label="ZIP Code"
                        name="shipping_postalCode"
                        placeholder="Enter ZIP code"
                        errors={formErrors}
                        value={postalCode}
                        setValue={setPostalCode}
                        disabled={loading}
                        required={true}
                      />
                      <Input
                        label="Phone (Optional)"
                        name="shipping_phone"
                        placeholder="Enter phone number (optional)"
                        errors={formErrors}
                        value={phone}
                        setValue={setPhone}
                        disabled={loading}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="w-full mt-10">
            <Button
              label="Update profile"
              disabled={
                loading ||
                !addressLine ||
                !city ||
                !stateProvinceCode ||
                !postalCode
              }
              theme="primary"
              size="medium"
              className="block w-full md:ml-auto md:w-auto"
              onClick={handleUpdateProfile}
            />

            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ProfileUpdate;
